import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { zonesApi, Zone, ZoneStatus, ZoneCreateData } from '../services/zonesApi';
import { api } from '../services/api';
import { linesApi, Line } from '../services/linesApi';
import { policiesApi, Policy } from '../services/policiesApi';
import { zoneFeaturesApi, FeatureTemplate, ZoneFeatureAssignment } from '../services/zoneFeaturesApi';
import { type ZoneTableRowModel } from '../components/zones/ZoneTable';
import ZonesPageContent from '../components/zones/ZonesPageContent';
import { CONFIG_SCOPE_LABELS, CONFIG_STATE_LABELS } from '../utils/configVocabulary';
import { useMode } from '../contexts/ModeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  canAccessWarehouseSurface,
  getCapabilitiesFromUser,
  hasSetupWriteAccess,
  isReadOnlyEvaluator,
} from '../auth/access';
import { sitesApi, type SiteComplexity } from '../services/sitesApi';
import { scheduleDeferredWork } from '../utils/deferredWork';
import { ensureUiScope } from '../services/api';

// YAML-like format for zones
const zoneToYaml = (zone: Partial<Zone> | ZoneCreateData): string => {
  const minDwellSeconds =
    typeof (zone as any).min_dwell_seconds === 'number' ? (zone as any).min_dwell_seconds : undefined;
  const cooldownSeconds =
    typeof (zone as any).cooldown_seconds === 'number' ? (zone as any).cooldown_seconds : undefined;
  const persistenceSeconds =
    typeof (zone as any).persistence_seconds === 'number' ? (zone as any).persistence_seconds : undefined;

  const lines = [
    `# Zone Configuration`,
    `zone_id: "${zone.zone_id || ''}"`,
    `name: "${zone.name || ''}"`,
    `device_id: "${zone.device_id || 'rtsp_cam'}"`,
    `camera_id: "${zone.camera_id || 'rtsp_cam'}"`,
    ``,
    `# Position: [x, y, width, height] in normalized coordinates (0.0-1.0)`,
    `bbox_norm: [${(zone.bbox_norm || [0, 0, 0.5, 1.0]).join(', ')}]`,
    ``,
    `# Display color (hex or CSS color)`,
    `color: "${zone.color || '#00FFFF'}"`,
    ``,
    `# Timing knobs (seconds)`,
    `# - min_dwell_seconds: how long an entity must be in zone before violation triggers`,
    `# - cooldown_seconds: minimum time between repeated incidents for the same alert type`,
    `# - persistence_seconds: require stable tracking for this long before emitting`,
    `min_dwell_seconds: ${minDwellSeconds ?? 2}`,
    `cooldown_seconds: ${cooldownSeconds ?? 300}`,
    `persistence_seconds: ${persistenceSeconds ?? 3}`,
    ``,
    `# Zone settings`,
    `# zone_type is descriptive context for policies and reporting; intrusion behavior is configured in policies.`,
    `zone_type: "${zone.zone_type || zone.type || 'work_area'}"`,
    `severity: "${zone.severity || 'medium'}"`,
    `max_occupancy: ${zone.max_occupancy ?? 10}`,
    ``,
    `# Alert types (list)`,
    `alerts:`,
    ...((zone.alerts || []).length > 0 
      ? (zone.alerts || []).map(a => `  - "${a}"`)
      : ['  - "ppe_violation"', '  - "zone_breach"']),
  ];
  return lines.join('\n');
};

const yamlToZone = (yaml: string): ZoneCreateData => {
  const data: any = {
    device_id: 'rtsp_cam',
    camera_id: 'rtsp_cam',
    bbox_norm: [0, 0, 0.5, 1.0],
    alerts: [],
  };
  
  const lines = yaml.split('\n');
  let currentList: string | null = null;
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('#') || !trimmed) continue;
    
    // Handle list items
    if (trimmed.startsWith('- ')) {
      const value = trimmed.substring(2).replace(/^["']|["']$/g, '').trim();
      if (currentList && value && !value.startsWith('#')) {
        data[currentList] = data[currentList] || [];
        data[currentList].push(value);
      }
      continue;
    }
    
    // Handle key-value pairs
    const colonIndex = trimmed.indexOf(':');
    if (colonIndex > 0) {
      const key = trimmed.substring(0, colonIndex).trim();
      let value = trimmed.substring(colonIndex + 1).trim();
      
      // Check if this starts a list
      if (!value || value === '') {
        currentList = key;
        continue;
      }
      
      currentList = null;
      
      // Parse value
      if (value.startsWith('[') && value.endsWith(']')) {
        // Array value
        data[key] = value
          .slice(1, -1)
          .split(',')
          .map(v => {
            const trimmed = v.trim();
            const num = parseFloat(trimmed);
            return isNaN(num) ? trimmed.replace(/^["']|["']$/g, '') : num;
          });
      } else if (value === 'true') {
        data[key] = true;
      } else if (value === 'false') {
        data[key] = false;
      } else if (!isNaN(parseFloat(value)) && !value.startsWith('"')) {
        data[key] = parseFloat(value);
      } else {
        data[key] = value.replace(/^["']|["']$/g, '');
      }
    }
  }
  
  return data as ZoneCreateData;
};

type ZoneStatusFilter = 'all' | 'active' | 'draft' | 'needs_policy' | 'needs_setup' | 'error';
type SnapshotFilter = 'all' | 'configured' | 'missing';
type PolicyFilter = 'all' | 'has_policies' | 'no_policies';

type ZoneRowModel = ZoneTableRowModel & {
  geometryConfigured: boolean;
  hasPolicies: boolean | null;
  policyCount: number | null;
  updatedAtRaw: string;
};

const titleCase = (value: string): string =>
  String(value || '')
    .trim()
    .replace(/_/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const ts = Date.parse(value);
  if (!Number.isFinite(ts)) return '—';
  return new Date(ts).toLocaleString();
};

const zoneHasGeometry = (zone: Zone | null | undefined): boolean => {
  if (!zone) return false;
  const bbox = Array.isArray(zone.bbox_norm) ? zone.bbox_norm : [];
  const polygon = Array.isArray((zone as any).polygon_norm) ? ((zone as any).polygon_norm as number[][]) : [];
  return bbox.length === 4 || polygon.length >= 3;
};

const Zones: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode } = useMode();
  const { effectiveUser: user } = useAuth();
  const capabilities = getCapabilitiesFromUser(user);
  const canReadZones = canAccessWarehouseSurface(capabilities, 'zones');
  const canWriteZones = hasSetupWriteAccess(capabilities) && !isReadOnlyEvaluator(user, 'zones');
  const query = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);

  // Use scoped site (localStorage/JWT) so cameras for devices like rubik_pi show up.
  const [scopeNonce, setScopeNonce] = useState(0);
  const SITE_ID = useMemo(() => {
    try {
      const scope = ensureUiScope();
      const s = String(scope.siteId || '').trim();
      if (s) return s;
    } catch {}
    return process.env.REACT_APP_SITE_ID || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scopeNonce triggers re-read when user changes scope
  }, [scopeNonce]);
  const initialDeviceId = (query.get('device_id') || '').trim() || undefined;
  const initialCameraId = (query.get('camera_id') || '').trim() || undefined;
  const returnTo = (query.get('return_to') || '').trim().toLowerCase();
  const returnDevice = (query.get('return_device') || '').trim();
  const returnStepRaw = (query.get('return_step') || '').trim();
  const returnStep =
    Number.isFinite(Number(returnStepRaw)) && returnStepRaw !== ''
      ? Math.max(0, Math.floor(Number(returnStepRaw)))
      : 2;
  const shouldAutoOpenSnapshot = ['1', 'true', 'yes'].includes(
    (query.get('open_snapshot') || '').trim().toLowerCase()
  );

  const [zones, setZones] = useState<Zone[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [policiesLoaded, setPoliciesLoaded] = useState(false);
  const [zoneStatuses, setZoneStatuses] = useState<ZoneStatus[]>([]);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [selectedZoneStatus, setSelectedZoneStatus] = useState<ZoneStatus | null>(null);
  const [selectedZoneLoading, setSelectedZoneLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [zonesLoaded, setZonesLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const initialLoadDoneRef = useRef(false);

  // Pilot-mode governance (site-level)
  const [siteComplexity, setSiteComplexity] = useState<SiteComplexity | null>(null);

  // Zone notification cadence (ticketing/digest behavior)
  const [cadenceSaving, setCadenceSaving] = useState(false);
  const [cadenceValue, setCadenceValue] = useState<'realtime' | 'daily' | 'weekly' | 'none'>('realtime');
  const [cadenceError, setCadenceError] = useState<string | null>(null);

  // Zone classification / risk level
  const [classSaving, setClassSaving] = useState(false);
  const [zoneClassValue, setZoneClassValue] = useState<'general' | 'high_risk'>('general');
  const [riskLevelValue, setRiskLevelValue] = useState<'' | 'low' | 'medium' | 'high' | 'critical'>('');
  const [classError, setClassError] = useState<string | null>(null);
  
  // Dialog states
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'create' | 'edit'>('create');
  const [yamlContent, setYamlContent] = useState('');
  const [yamlError, setYamlError] = useState<string | null>(null);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  
  // Push dialog
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [pushDeviceId, setPushDeviceId] = useState('rtsp_cam');
  const [pushing, setPushing] = useState(false);

  // Phase D1: snapshot editor dialog
  const [snapshotEditorOpen, setSnapshotEditorOpen] = useState(false);
  const snapshotAutoOpenedRef = useRef(false);

  // Block auto-refresh (interval, scope-changed) while save/deploy/apply is in progress
  const blockRefreshRef = useRef(false);
  const zonesFetchGenRef = useRef(0);
  const [pauseAutoRefresh, setPauseAutoRefresh] = useState(false);

  // Zone Features (Phase 1 MVP)
  const [featureTemplates, setFeatureTemplates] = useState<FeatureTemplate[]>([]);
  const [zoneFeatures, setZoneFeatures] = useState<ZoneFeatureAssignment[]>([]);
  const [applyFeatureOpen, setApplyFeatureOpen] = useState(false);
  const [applyTemplateId, setApplyTemplateId] = useState<string>('');
  const [applyDeployNow, setApplyDeployNow] = useState(true);
  const [applyEnabled, setApplyEnabled] = useState(true);
  const [applyMinDwellMs, setApplyMinDwellMs] = useState<number>(2000);
  const [applyCooldownMs, setApplyCooldownMs] = useState<number>(60000);
  const [applySeverity, setApplySeverity] = useState<string>('high');
  const [applyingFeature, setApplyingFeature] = useState(false);
  
  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
    autoHideDuration?: number;
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  /** When true, list includes cloud-disabled rows (soft-deleted zones stay visible). */
  const [showDisabledZones, setShowDisabledZones] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [zonePendingDelete, setZonePendingDelete] = useState<Zone | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Zone actions menus
  const [zoneMenuAnchor, setZoneMenuAnchor] = useState<null | HTMLElement>(null);
  const [zoneMenuZone, setZoneMenuZone] = useState<Zone | null>(null);
  const [pageMenuAnchor, setPageMenuAnchor] = useState<null | HTMLElement>(null);

  // Table / filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ZoneStatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [snapshotFilter, setSnapshotFilter] = useState<SnapshotFilter>('all');
  const [policyFilter, setPolicyFilter] = useState<PolicyFilter>('all');
  const [linesExpanded, setLinesExpanded] = useState(false);

  const fetchData = useCallback(async (force?: boolean, opts?: { includeDisabled?: boolean }) => {
      if (!force && (blockRefreshRef.current || pauseAutoRefresh)) return;
      const gen = ++zonesFetchGenRef.current;
      const isInitialLoad = !initialLoadDoneRef.current;
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
    try {
      const scope = ensureUiScope();
      const q = new URLSearchParams(location.search || '');
      const orgId = String(scope.orgId || q.get('org_id') || '').trim();
      const siteId = String(scope.siteId || q.get('site') || q.get('site_id') || '').trim();
      if (!orgId || !siteId) {
        setZones([]);
        setZoneStatuses([]);
        setLines([]);
        setPolicies([]);
        setPoliciesLoaded(true);
        setZonesLoaded(true);
        setSnackbar({
          open: true,
          message: 'Org/site scope is not set yet. Re-login or choose scope from the header.',
          severity: 'error',
        });
        setLoading(false);
        setRefreshing(false);
        initialLoadDoneRef.current = true;
        return;
      }
      const params = new URLSearchParams(location.search || '');
      const deviceIdParam = (params.get('device_id') || '').trim() || undefined;
      const cameraIdParam = (params.get('camera_id') || '').trim() || undefined;
      const moduleScope = mode === 'ocr' ? 'ocr' : 'warehouse';
      const includeDisabled = opts?.includeDisabled ?? showDisabledZones;
      setPoliciesLoaded(false);
      if (isInitialLoad) setZonesLoaded(false);
      const [zonesResult, statusResult, linesResult] = await Promise.allSettled([
        // include_disabled: when false, soft-deleted (disabled) zones are omitted so Delete removes cards from this grid.
        zonesApi.listZones(
          undefined,
          undefined,
          deviceIdParam,
          cameraIdParam,
          includeDisabled,
          moduleScope,
          orgId,
          siteId
        ),
        zonesApi.getAllZonesStatus({
          orgId,
          siteId,
          deviceId: deviceIdParam,
          cameraId: cameraIdParam,
        }),
        linesApi.listLines(deviceIdParam, cameraIdParam),
      ]);
      if (gen !== zonesFetchGenRef.current) return;
      if (zonesResult.status === 'fulfilled') {
        setZones(zonesResult.value.zones || []);
      } else {
        console.error('Failed to fetch zones:', zonesResult.reason);
      }
      if (statusResult.status === 'fulfilled') {
        setZoneStatuses(statusResult.value.zones || []);
      } else {
        console.error('Failed to fetch zone status:', statusResult.reason);
        setZoneStatuses([]);
      }
      if (linesResult.status === 'fulfilled') {
        setLines(linesResult.value.lines || []);
      } else {
        console.error('Failed to fetch lines:', linesResult.reason);
        setLines([]);
      }
      if (deviceIdParam) {
        setPushDeviceId(deviceIdParam);
      }

      scheduleDeferredWork(() => {
        if (gen !== zonesFetchGenRef.current) return;
        void (async () => {
          try {
            const policiesRes = await policiesApi.listPolicies(siteId, true, moduleScope);
            if (gen !== zonesFetchGenRef.current) return;
            setPolicies(policiesRes.policies || []);
            setPoliciesLoaded(true);
          } catch (err) {
            if (gen !== zonesFetchGenRef.current) return;
            console.error('Failed to fetch policies (deferred):', err);
            setPoliciesLoaded(true);
          }
          try {
            const c = await sitesApi.getComplexity(siteId);
            if (gen !== zonesFetchGenRef.current) return;
            setSiteComplexity(c);
          } catch {
            // optional (auth or legacy env); ignore
          }
        })();
      });
    } catch (err) {
      console.error('Failed to fetch zones:', err);
      setPoliciesLoaded(true);
    } finally {
      if (gen === zonesFetchGenRef.current) {
        setZonesLoaded(true);
        setLoading(false);
        setRefreshing(false);
        initialLoadDoneRef.current = true;
      }
    }
  }, [location.search, mode, pauseAutoRefresh, showDisabledZones]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const onScopeChanged = () => {
      setScopeNonce((n) => n + 1);
      fetchData();
    };
    window.addEventListener('edgentik:scope-changed', onScopeChanged);
    return () => window.removeEventListener('edgentik:scope-changed', onScopeChanged);
  }, [fetchData]);

  useEffect(() => {
    if (!shouldAutoOpenSnapshot) return;
    if (snapshotAutoOpenedRef.current) return;
    snapshotAutoOpenedRef.current = true;
    setSnapshotEditorOpen(true);
  }, [shouldAutoOpenSnapshot]);

  const handleBackFromZones = () => {
    if (returnTo === 'setup') {
      const q = new URLSearchParams();
      const targetDevice = returnDevice || initialDeviceId;
      if (targetDevice) {
        q.set('device', targetDevice);
      }
      q.set('step', String(returnStep));
      navigate(`/setup?${q.toString()}`);
      return;
    }
    navigate(-1);
  };

  const handleZoneSelect = async (zone: Zone) => {
    setSelectedZone(zone);
    setSelectedZoneLoading(true);
    setSelectedZoneStatus(null);
    setZoneFeatures([]);
    try {
      const [statusRes, featureRes] = await Promise.allSettled([
        zonesApi.getZoneStatus(zone.id),
        zoneFeaturesApi.listZoneAssignments(zone.id),
      ]);
      if (statusRes.status === 'fulfilled') {
        setSelectedZoneStatus(statusRes.value);
      } else {
        console.error('Failed to fetch zone status:', statusRes.reason);
      }
      if (featureRes.status === 'fulfilled') {
        setZoneFeatures(featureRes.value.assignments || []);
      } else {
        // Best-effort: older envs may not have schema yet
        console.warn('Failed to fetch zone features:', featureRes.reason);
        setZoneFeatures([]);
      }
    } finally {
      setSelectedZoneLoading(false);
    }
  };

  const handleCloseZoneDrawer = () => {
    setSelectedZone(null);
    setSelectedZoneStatus(null);
    setZoneFeatures([]);
    setSelectedZoneLoading(false);
  };

  const ensureTemplatesLoaded = useCallback(async () => {
    if (featureTemplates.length > 0) return;
    try {
      const res = await zoneFeaturesApi.listTemplates(true);
      setFeatureTemplates(res.templates || []);
    } catch (err) {
      console.warn('Failed to fetch feature templates:', err);
    }
  }, [featureTemplates.length]);

  const openApplyFeature = useCallback(async () => {
    if (!selectedZone) return;
    await ensureTemplatesLoaded();
    const first = (featureTemplates[0] as any) || null;
    const tid = String(first?.id || '');
    setApplyTemplateId(tid);
    const defaults = (first?.default_parameters || {}) as any;
    setApplyEnabled(true);
    setApplyDeployNow(true);
    setApplyMinDwellMs(Number(defaults?.min_dwell_ms ?? 2000));
    setApplyCooldownMs(Number(defaults?.cooldown_ms ?? 60000));
    setApplySeverity(String(defaults?.severity ?? 'high'));
    setApplyFeatureOpen(true);
  }, [ensureTemplatesLoaded, featureTemplates, selectedZone]);

  const applyFeature = useCallback(async () => {
    if (!selectedZone) return;
    if (!applyTemplateId) {
      setSnackbar({ open: true, message: 'Select a feature template.', severity: 'error' });
      return;
    }
    setApplyingFeature(true);
    blockRefreshRef.current = true;
    try {
      const overrides: Record<string, any> = {
        min_dwell_ms: applyMinDwellMs,
        cooldown_ms: applyCooldownMs,
        severity: applySeverity,
      };
      await zoneFeaturesApi.createAssignment(selectedZone.id, {
        feature_template_id: applyTemplateId,
        feature_template_version: '1.0.0',
        enabled: applyEnabled,
        parameter_overrides: overrides,
      });
      const feats = await zoneFeaturesApi.listZoneAssignments(selectedZone.id);
      setZoneFeatures(feats.assignments || []);
      setSnackbar({ open: true, message: 'Feature applied to zone.', severity: 'success' });
      setApplyFeatureOpen(false);
      if (applyDeployNow) {
        const dep = await policiesApi.deployAllPolicies('default');
        setSnackbar({
          open: true,
          message: dep?.deployment_id ? `Deployment ${dep.deployment_id} started.` : 'Deployment started.',
          severity: 'success',
        });
        navigate('/deployments');
      }
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || err.message || 'Failed to apply feature', severity: 'error' });
    } finally {
      setApplyingFeature(false);
      blockRefreshRef.current = false;
    }
  }, [applyCooldownMs, applyDeployNow, applyEnabled, applyMinDwellMs, applySeverity, applyTemplateId, navigate, selectedZone]);

  useEffect(() => {
    const v = String((selectedZone as any)?.notification_cadence || '').trim().toLowerCase();
    if (v === 'daily' || v === 'weekly' || v === 'none' || v === 'realtime') {
      setCadenceValue(v as any);
    } else {
      setCadenceValue('realtime');
    }
    setCadenceError(null);
    setCadenceSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedZone.id sufficient
  }, [selectedZone?.id]);

  useEffect(() => {
    const cls = String((selectedZone as any)?.zone_class || '').trim().toLowerCase();
    if (cls === 'high_risk' || cls === 'general') {
      setZoneClassValue(cls as any);
    } else {
      setZoneClassValue((selectedZone as any)?.restricted ? 'high_risk' : 'general');
    }
    const rl = String((selectedZone as any)?.risk_level || '').trim().toLowerCase();
    if (rl === 'low' || rl === 'medium' || rl === 'high' || rl === 'critical') {
      setRiskLevelValue(rl as any);
    } else {
      setRiskLevelValue('');
    }
    setClassError(null);
    setClassSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedZone.id sufficient
  }, [selectedZone?.id]);

  const removeAssignment = useCallback(
    async (assignmentId: string) => {
      if (!selectedZone) return;
      if (!window.confirm('Remove this feature from the zone?')) return;
      try {
        await zoneFeaturesApi.deleteAssignment(assignmentId);
        const feats = await zoneFeaturesApi.listZoneAssignments(selectedZone.id);
        setZoneFeatures(feats.assignments || []);
        setSnackbar({ open: true, message: 'Feature removed.', severity: 'success' });
      } catch (err: any) {
        setSnackbar({ open: true, message: err?.response?.data?.error || err.message || 'Failed to remove feature', severity: 'error' });
      }
    },
    [selectedZone],
  );

  const getStatus = useCallback(
    (zone: Zone) =>
      zoneStatuses.find(
        (s) => s.zone_id === zone.zone_id || s.zone_id === zone.name || s.name === zone.name,
      ),
    [zoneStatuses],
  );

  const policyUsage = useMemo(() => {
    const enabled = (policies || []).filter((p) => p && p.enabled !== false && p.status !== 'deleted');
    const zoneCount: Record<string, number> = {};
    const lineCount: Record<string, number> = {};
    for (const p of enabled) {
      for (const zid of p.scope?.zone_ids || []) {
        zoneCount[zid] = (zoneCount[zid] || 0) + 1;
      }
      const lineId = (p as any)?.config?.line_id || (p as any)?.config?.line?.line_id;
      if (p.policy_type === 'line_crossing' && lineId) {
        lineCount[lineId] = (lineCount[lineId] || 0) + 1;
      }
    }
    return { zoneCount, lineCount };
  }, [policies]);

  const goToPolicies = useCallback((params: Record<string, string>) => {
    const usp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v) usp.set(k, v);
    }
    navigate(`/policies?${usp.toString()}`);
  }, [navigate]);

  const openZonePolicies = useCallback(
    (zone: Zone) => {
      goToPolicies({
        camera_id: zone.camera_id || '',
        zone_id: zone.zone_id,
      });
    },
    [goToPolicies],
  );

  const createPolicyForZone = useCallback(
    (zone: Zone) => {
      goToPolicies({
        create: '1',
        type: 'zone_intrusion',
        camera_id: zone.camera_id || '',
        zone_id: zone.zone_id,
      });
    },
    [goToPolicies],
  );

  const handleDuplicateZone = useCallback((zone: Zone) => {
    setZoneMenuAnchor(null);
    setZoneMenuZone(null);
    setEditorMode('create');
    setEditingZone(null);
    const nextName = zone.name ? `${zone.name} Copy` : `${zone.zone_id || 'zone'} Copy`;
    const nextZoneId = `${zone.zone_id || zone.name || 'zone'}_copy`;
    setYamlContent(
      zoneToYaml({
        ...(zone as any),
        name: nextName,
        zone_id: nextZoneId,
      }),
    );
    setYamlError(null);
    setEditorOpen(true);
  }, []);

  const handleValidateZone = useCallback(
    async (zone: Zone) => {
      setZoneMenuAnchor(null);
      setZoneMenuZone(null);
      try {
        const scope = ensureUiScope();
        const orgId = String(scope.orgId || '').trim() || undefined;
        const deviceId = String(zone.device_id || initialDeviceId || pushDeviceId || '').trim() || undefined;
        const result = await zonesApi.validateZones(SITE_ID, {
          org_id: orgId,
          edge_device_id: deviceId,
          camera_id: zone.camera_id || undefined,
          zone_ids: zone.zone_id ? [zone.zone_id] : undefined,
        });
        if (result?.ok === true) {
          setSnackbar({
            open: true,
            message: `Zone "${zone.name || zone.zone_id}" passed validation.`,
            severity: 'success',
          });
          return;
        }
        const errors = Number(result?.summary?.errors || 0);
        setSnackbar({
          open: true,
          message:
            errors > 0
              ? `Validation found ${errors} error${errors === 1 ? '' : 's'} for "${zone.name || zone.zone_id}".`
              : `Validation failed for "${zone.name || zone.zone_id}".`,
          severity: 'error',
        });
      } catch (err: any) {
        setSnackbar({
          open: true,
          message: err?.response?.data?.error || err?.message || 'Failed to validate zone',
          severity: 'error',
        });
      }
    },
    [SITE_ID, initialDeviceId, pushDeviceId],
  );

  const openPushForZone = useCallback((zone?: Zone | null) => {
    setZoneMenuAnchor(null);
    setZoneMenuZone(null);
    setPushDeviceId(String(zone?.device_id || initialDeviceId || 'rtsp_cam'));
    setPushDialogOpen(true);
  }, [initialDeviceId]);

  // Editor handlers
  const handleOpenCreate = () => {
    setEditorMode('create');
    setEditingZone(null);
    setYamlContent(zoneToYaml({
      zone_id: 'new_zone',
      name: 'New Zone',
      device_id: 'rtsp_cam',
      camera_id: 'rtsp_cam',
      bbox_norm: [0, 0, 0.5, 1.0],
      color: '#00FFFF',
      zone_type: 'work_area',
      restricted: false,
      severity: 'medium',
      max_occupancy: 10,
      alerts: ['ppe_violation'],
    }));
    setYamlError(null);
    setEditorOpen(true);
  };

  const handleOpenEdit = (zone: Zone) => {
    setEditorMode('edit');
    setEditingZone(zone);
    setYamlContent(zoneToYaml(zone));
    setYamlError(null);
    setEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
    setEditingZone(null);
    setYamlContent('');
    setYamlError(null);
  };

  const handleSaveZone = async () => {
    blockRefreshRef.current = true;
    try {
      const zoneData = yamlToZone(yamlContent);
      const module_scope = mode === 'ocr' ? 'ocr' : 'warehouse';
      
      // Validate required fields
      if (!zoneData.zone_id || !zoneData.name) {
        setYamlError('zone_id and name are required');
        return;
      }
      
      if (editorMode === 'create') {
        await zonesApi.createZone({ ...(zoneData as any), module_scope });
        setSnackbar({ open: true, message: `Zone "${zoneData.name}" created!`, severity: 'success' });
      } else if (editingZone) {
        await zonesApi.updateZone(editingZone.id, { ...(zoneData as any), module_scope });
        setSnackbar({ open: true, message: `Zone "${zoneData.name}" updated!`, severity: 'success' });
      }
      
      handleCloseEditor();
      await fetchData(true);
    } catch (err: any) {
      setYamlError(err.response?.data?.error || err.message || 'Failed to save zone');
    } finally {
      blockRefreshRef.current = false;
    }
  };

  const beginDeleteZone = (zone: Zone) => {
    setZoneMenuAnchor(null);
    setZoneMenuZone(null);
    setZonePendingDelete(zone);
    setDeleteDialogOpen(true);
  };

  const handleDeleteZone = async (zone: Zone) => {
    const zoneId = zone.id || zone.zone_id;
    if (!zoneId) {
      setSnackbar({ open: true, message: 'Cannot delete: zone has no id', severity: 'error' });
      return;
    }
    blockRefreshRef.current = true;
    setDeleteSubmitting(true);
    try {
      const scope = ensureUiScope();
      const q = new URLSearchParams(location.search || '');
      const orgId = String(scope.orgId || q.get('org_id') || '').trim() || 'default';
      const siteId = String(scope.siteId || q.get('site') || q.get('site_id') || '').trim() || SITE_ID;
      await zonesApi.deleteZone(zoneId, orgId, siteId);
      setShowDisabledZones(false);
      setSnackbar({
        open: true,
        severity: 'success',
        autoHideDuration: 10_000,
        message: `Zone "${zone.name}" was disabled in cloud. Showing active zones only — check "Show disabled zones" to audit disabled rows. Push to edge if video overlays should update.`,
      });
      if (selectedZone?.id === zone.id) {
        setSelectedZone(null);
        setSelectedZoneStatus(null);
      }
      // Refetch with includeDisabled false immediately (React state would not update before fetch otherwise).
      await fetchData(true, { includeDisabled: false });
    } catch (err: any) {
      setSnackbar({
        open: true,
        severity: 'error',
        autoHideDuration: 8000,
        message: err.response?.data?.error || err.message || 'Failed to delete zone',
      });
    } finally {
      setDeleteSubmitting(false);
      blockRefreshRef.current = false;
      setDeleteDialogOpen(false);
      setZonePendingDelete(null);
    }
  };

  // Push handlers
  const handleOpenPush = () => {
    openPushForZone(null);
  };

  const handlePushToEdge = async () => {
    setPushing(true);
    blockRefreshRef.current = true;
    try {
      // IMPORTANT:
      // Do NOT use legacy /api/v1/zones/push here. That endpoint historically used epoch bundle_version
      // which can permanently "poison" edge-side monotonic checks and make newer Phase D deploys look stale.
      // Always use the deployment-correlated Phase D endpoint.

      // Validate first (fail fast with actionable errors).
      const scope = ensureUiScope();
      const orgIdForValidate = String(scope.orgId || '').trim() || undefined;
      const v = await zonesApi.validateZones(SITE_ID, {
        org_id: orgIdForValidate,
        edge_device_id: pushDeviceId,
      });
      if (!v || v.ok !== true) {
        const n = Number(v?.summary?.errors || 0);
        setSnackbar({
          open: true,
          message: n > 0 ? `Deploy blocked: validation failed (${n} error${n === 1 ? '' : 's'}).` : 'Deploy blocked: validation failed.',
          severity: 'error',
        });
        return;
      }

      // Gate 4 arming: record validation pass + arm monitoring, then deploy.
      const orgId = String(v?.org_id || 'default');
      try {
        await api.post(`/api/v1/sites/${SITE_ID}/validation/runs`, {
          org_id: orgId,
          device_id: pushDeviceId,
          status: 'pass',
          details: { source: 'zones_page_push' },
        });
      } catch {
        // Best-effort.
      }
      try {
        await api.post(`/api/v1/sites/${SITE_ID}/arming`, {
          org_id: orgId,
          device_id: pushDeviceId,
          monitoring_enabled: true,
          require_validation_pass: true,
        });
      } catch (e: any) {
        const status = e?.response?.status;
        const errCode = e?.response?.data?.error;
        if (status === 409 && errCode === 'arming_requires_validation_pass') {
          await api.post(`/api/v1/sites/${SITE_ID}/arming`, {
            org_id: orgId,
            device_id: pushDeviceId,
            monitoring_enabled: true,
            require_validation_pass: false,
          });
        } else if (status === 409 && errCode) {
          setSnackbar({ open: true, message: `Deploy blocked: could not arm monitoring (${errCode}).`, severity: 'error' });
          return;
        } else {
          throw e;
        }
      }

      const dep = await zonesApi.deployZones(SITE_ID, { org_id: orgId, edge_device_id: pushDeviceId });
      const depId = dep?.deployment_id ? String(dep.deployment_id) : '';
      const count = typeof dep?.count === 'number' ? dep.count : null;
      setSnackbar({
        open: true,
        message: depId
          ? `Zones deployment started (deployment ${depId})${count !== null ? ` — ${count} camera(s)` : ''}.`
          : 'Zones deployment started.',
        severity: 'success',
      });
      setPushDialogOpen(false);
    } catch (err: any) {
      const status = err?.response?.status;
      const errCode = err?.response?.data?.error;
      const detail = err?.response?.data?.detail;
      const msg =
        status && errCode
          ? `Deploy error (${status}): ${errCode}${detail ? ` — ${String(detail).slice(0, 200)}` : ''}`
          : err?.response?.data?.error || err?.message || 'Failed to deploy zones';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setPushing(false);
      blockRefreshRef.current = false;
    }
  };

  const zoneRows = useMemo<ZoneRowModel[]>(() => {
    const priority: Record<string, number> = {
      [CONFIG_STATE_LABELS.blocked]: 0,
      [CONFIG_STATE_LABELS.needsAction]: 1,
      [CONFIG_STATE_LABELS.draft]: 2,
      [CONFIG_STATE_LABELS.live]: 3,
    };

    return [...zones]
      .map((zone) => {
        const status = getStatus(zone);
        const geometryConfigured = zoneHasGeometry(zone);
        const policyCount = policiesLoaded ? policyUsage.zoneCount[zone.zone_id] || 0 : null;
        const hasPolicies = policyCount === null ? null : policyCount > 0;
        const updatedAtRaw = String((zone as any).updated_at || status?.timestamp || '').trim();

        let statusLabel: string = CONFIG_STATE_LABELS.live;
        if (zone.enabled === false) {
          statusLabel = CONFIG_STATE_LABELS.draft;
        } else if (!geometryConfigured) {
          statusLabel = CONFIG_STATE_LABELS.needsAction;
        } else if (hasPolicies === false) {
          statusLabel = CONFIG_STATE_LABELS.needsAction;
        } else if (['overcrowded', 'blocked', 'alert'].includes(String(status?.status || '').toLowerCase())) {
          statusLabel = CONFIG_STATE_LABELS.blocked;
        }

        const displayName = String(zone.name || zone.zone_id || zone.id || 'Unnamed zone');
        const secondaryId =
          zone.zone_id && zone.zone_id !== zone.name
            ? zone.zone_id
            : zone.id && zone.id !== zone.zone_id
              ? zone.id
              : '';

        return {
          id: zone.id,
          zone,
          status,
          displayName,
          secondaryId,
          targetDeviceLabel: String(zone.device_id || '').trim() || CONFIG_SCOPE_LABELS.noEdgeDevice,
          targetCameraLabel: String(zone.camera_id || '').trim() || CONFIG_SCOPE_LABELS.noCameraStream,
          typeLabel: titleCase(zone.zone_type || zone.type || 'unknown'),
          snapshotLabel: geometryConfigured ? CONFIG_STATE_LABELS.configured : CONFIG_STATE_LABELS.missing,
          policyLabel:
            policyCount === null
              ? 'Loading…'
              : policyCount > 0
                ? `${policyCount} ${CONFIG_STATE_LABELS.live.toLowerCase()}`
                : 'No policies',
          occupancyLabel: `${status?.occupancy ?? '-'} / ${(zone.max_occupancy ?? 0) > 0 ? zone.max_occupancy : '—'}`,
          statusLabel,
          lastUpdatedLabel: formatDateTime(updatedAtRaw),
          geometryConfigured,
          hasPolicies,
          policyCount,
          updatedAtRaw,
        };
      })
      .sort((left, right) => {
        const statusDiff = (priority[left.statusLabel] ?? 99) - (priority[right.statusLabel] ?? 99);
        if (statusDiff !== 0) return statusDiff;
        return left.displayName.localeCompare(right.displayName);
      });
  }, [zones, getStatus, policyUsage.zoneCount, policiesLoaded]);

  const zoneTypeOptions = useMemo(
    () =>
      Array.from(new Set(zoneRows.map((row) => row.typeLabel).filter(Boolean))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [zoneRows],
  );

  const filteredZoneRows = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();

    return zoneRows.filter((row) => {
      if (search) {
        const haystack = [
          row.displayName,
          row.secondaryId,
          row.zone.camera_id || '',
          row.zone.device_id || '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }

      if (statusFilter !== 'all') {
        const matchesStatus =
          (statusFilter === 'active' && row.statusLabel === CONFIG_STATE_LABELS.live) ||
          (statusFilter === 'draft' && row.statusLabel === CONFIG_STATE_LABELS.draft) ||
          (statusFilter === 'needs_policy' && row.statusLabel === CONFIG_STATE_LABELS.needsAction && row.hasPolicies === false) ||
          (statusFilter === 'needs_setup' && row.statusLabel === CONFIG_STATE_LABELS.needsAction && !row.geometryConfigured) ||
          (statusFilter === 'error' && row.statusLabel === CONFIG_STATE_LABELS.blocked);
        if (!matchesStatus) return false;
      }

      if (typeFilter !== 'all' && row.typeLabel !== typeFilter) return false;

      if (snapshotFilter !== 'all') {
        if (snapshotFilter === 'configured' && !row.geometryConfigured) return false;
        if (snapshotFilter === 'missing' && row.geometryConfigured) return false;
      }

      if (policyFilter !== 'all' && policiesLoaded) {
        if (policyFilter === 'has_policies' && row.hasPolicies !== true) return false;
        if (policyFilter === 'no_policies' && row.hasPolicies !== false) return false;
      }

      return true;
    });
  }, [zoneRows, searchQuery, statusFilter, typeFilter, snapshotFilter, policyFilter, policiesLoaded]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setSnapshotFilter('all');
    setPolicyFilter('all');
  };

  const zoneSummary = useMemo(() => {
    const total = zoneRows.length;
    const active = zoneRows.filter((row) => row.zone.enabled !== false).length;
    const missingPolicies = policiesLoaded
      ? zoneRows.filter((row) => row.hasPolicies === false && row.zone.enabled !== false).length
      : null;
    const missingSetup = zoneRows.filter((row) => !row.geometryConfigured && row.zone.enabled !== false).length;
    const errorCount = zoneRows.filter((row) => row.statusLabel === CONFIG_STATE_LABELS.blocked).length;
    const lastLiveUpdateRaw = zoneRows
      .map((row) => row.updatedAtRaw)
      .filter(Boolean)
      .sort((left, right) => Date.parse(right) - Date.parse(left))[0];

    return {
      total,
      active,
      missingPolicies,
      missingSetup,
      errorCount,
      lineCount: lines.length,
      lastLiveUpdateLabel: formatDateTime(lastLiveUpdateRaw),
    };
  }, [zoneRows, policiesLoaded, lines.length]);

  const selectedZoneRow = useMemo(
    () => zoneRows.find((row) => row.zone.id === selectedZone?.id) || null,
    [zoneRows, selectedZone?.id],
  );

  const selectedZonePolicies = useMemo(() => {
    if (!selectedZone?.zone_id) return [];
    return (policies || []).filter(
      (policy) =>
        policy &&
        policy.enabled !== false &&
        policy.status !== 'deleted' &&
        Array.isArray(policy.scope?.zone_ids) &&
        policy.scope.zone_ids.includes(selectedZone.zone_id),
    );
  }, [policies, selectedZone?.zone_id]);

  const refreshZonesAfterSelectedZoneUpdate = useCallback(async () => {
    const res = await zonesApi.listZones(
      undefined,
      undefined,
      initialDeviceId,
      initialCameraId,
      showDisabledZones,
      mode === 'ocr' ? 'ocr' : 'warehouse',
    );
    const items = res?.zones || [];
    setZones(items);
    if (!selectedZone?.id) return;
    const next = items.find((z) => z.id === selectedZone.id) || null;
    setSelectedZone(next);
  }, [initialCameraId, initialDeviceId, mode, selectedZone?.id, showDisabledZones]);

  const saveClassification = useCallback(async () => {
    if (!selectedZone?.id) return;
    try {
      setClassSaving(true);
      setClassError(null);
      await zonesApi.updateZone(selectedZone.id, {
        zone_class: zoneClassValue,
        risk_level: riskLevelValue ? riskLevelValue : null,
        restricted: false,
      } as any);
      await refreshZonesAfterSelectedZoneUpdate();
    } catch (e: any) {
      setClassError(String(e?.message || e || 'Failed to update classification'));
    } finally {
      setClassSaving(false);
    }
  }, [refreshZonesAfterSelectedZoneUpdate, riskLevelValue, selectedZone?.id, zoneClassValue]);

  const saveCadence = useCallback(async () => {
    if (!selectedZone?.id) return;
    try {
      setCadenceSaving(true);
      setCadenceError(null);
      await zonesApi.updateZone(selectedZone.id, { notification_cadence: cadenceValue });
      await refreshZonesAfterSelectedZoneUpdate();
    } catch (e: any) {
      setCadenceError(String(e?.message || e || 'Failed to update cadence'));
    } finally {
      setCadenceSaving(false);
    }
  }, [cadenceValue, refreshZonesAfterSelectedZoneUpdate, selectedZone?.id]);

  return (
    <ZonesPageContent
      canReadZones={canReadZones}
      canWriteZones={canWriteZones}
      zoneSummary={zoneSummary}
      setSnapshotEditorOpen={setSnapshotEditorOpen}
      handleOpenCreate={handleOpenCreate}
      handleOpenPush={handleOpenPush}
      setPageMenuAnchor={setPageMenuAnchor}
      pageMenuAnchor={pageMenuAnchor}
      siteComplexity={siteComplexity}
      refreshing={refreshing}
      loading={loading}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      typeFilter={typeFilter}
      setTypeFilter={setTypeFilter}
      zoneTypeOptions={zoneTypeOptions}
      snapshotFilter={snapshotFilter}
      setSnapshotFilter={setSnapshotFilter}
      policyFilter={policyFilter}
      setPolicyFilter={setPolicyFilter}
      policiesLoaded={policiesLoaded}
      clearFilters={clearFilters}
      zonesLoaded={zonesLoaded}
      filteredZoneRows={filteredZoneRows}
      selectedZone={selectedZone}
      handleZoneSelect={handleZoneSelect}
      setZoneMenuAnchor={setZoneMenuAnchor}
      setZoneMenuZone={setZoneMenuZone}
      createPolicyForZone={createPolicyForZone}
      zoneRows={zoneRows}
      linesExpanded={linesExpanded}
      setLinesExpanded={setLinesExpanded}
      lines={lines}
      policyUsage={policyUsage}
      formatDateTime={formatDateTime}
      goToPolicies={goToPolicies}
      handleCloseZoneDrawer={handleCloseZoneDrawer}
      selectedZoneRow={selectedZoneRow}
      selectedZoneStatus={selectedZoneStatus}
      selectedZoneLoading={selectedZoneLoading}
      titleCase={titleCase}
      handleValidateZone={handleValidateZone}
      openZonePolicies={openZonePolicies}
      handleOpenEdit={handleOpenEdit}
      handleDuplicateZone={handleDuplicateZone}
      openPushForZone={openPushForZone}
      beginDeleteZone={beginDeleteZone}
      selectedZonePolicies={selectedZonePolicies}
      classError={classError}
      zoneClassValue={zoneClassValue}
      setZoneClassValue={setZoneClassValue}
      riskLevelValue={riskLevelValue}
      setRiskLevelValue={setRiskLevelValue}
      classSaving={classSaving}
      saveClassification={saveClassification}
      cadenceError={cadenceError}
      cadenceValue={cadenceValue}
      setCadenceValue={setCadenceValue}
      cadenceSaving={cadenceSaving}
      saveCadence={saveCadence}
      openApplyFeature={openApplyFeature}
      zoneFeatures={zoneFeatures}
      removeAssignment={removeAssignment}
      applyFeatureOpen={applyFeatureOpen}
      setApplyFeatureOpen={setApplyFeatureOpen}
      applyTemplateId={applyTemplateId}
      setApplyTemplateId={setApplyTemplateId}
      featureTemplates={featureTemplates}
      setApplyMinDwellMs={setApplyMinDwellMs}
      setApplyCooldownMs={setApplyCooldownMs}
      setApplySeverity={setApplySeverity}
      applyMinDwellMs={applyMinDwellMs}
      applyCooldownMs={applyCooldownMs}
      applySeverity={applySeverity}
      applyEnabled={applyEnabled}
      setApplyEnabled={setApplyEnabled}
      applyDeployNow={applyDeployNow}
      setApplyDeployNow={setApplyDeployNow}
      applyingFeature={applyingFeature}
      applyFeature={applyFeature}
      snapshotEditorOpen={snapshotEditorOpen}
      SITE_ID={SITE_ID}
      initialDeviceId={initialDeviceId}
      initialCameraId={initialCameraId}
      editorOpen={editorOpen}
      handleCloseEditor={handleCloseEditor}
      editorMode={editorMode}
      editingZone={editingZone}
      yamlError={yamlError}
      yamlContent={yamlContent}
      setYamlContent={setYamlContent}
      handleSaveZone={handleSaveZone}
      pushDialogOpen={pushDialogOpen}
      setPushDialogOpen={setPushDialogOpen}
      pushDeviceId={pushDeviceId}
      setPushDeviceId={setPushDeviceId}
      pushing={pushing}
      handlePushToEdge={handlePushToEdge}
      fetchData={fetchData}
      showDisabledZones={showDisabledZones}
      setShowDisabledZones={setShowDisabledZones}
      pauseAutoRefresh={pauseAutoRefresh}
      setPauseAutoRefresh={setPauseAutoRefresh}
      returnTo={returnTo}
      handleBackFromZones={handleBackFromZones}
      zoneMenuAnchor={zoneMenuAnchor}
      zoneMenuZone={zoneMenuZone}
      deleteDialogOpen={deleteDialogOpen}
      deleteSubmitting={deleteSubmitting}
      setDeleteDialogOpen={setDeleteDialogOpen}
      zonePendingDelete={zonePendingDelete}
      setZonePendingDelete={setZonePendingDelete}
      handleDeleteZone={handleDeleteZone}
      snackbar={snackbar}
      setSnackbar={setSnackbar}
    />
  );
};

export default Zones;
